import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { trim } from 'lodash';
import classnames from 'classnames';

import UnitListItem from 'components/unit/UnitList/UnitListItem';
import { UnitDescriptionV2 } from 'components/unit/UnitDescription';
import { UnitPriceV2 } from 'components/unit/UnitPrice';
import { UnitIcon } from 'components/unit/UnitIcon';
import { UnitReservationForm } from 'components/unit/UnitReservationForm';
import { Expand } from 'components/core/Expand';
import { IconText } from 'components/core/IconText';
import { getScarcityFlag } from 'utils/store/getScarcityFlag';
import { Card } from 'components/core/Card';
import { Paragraph } from 'components/core/Typography';

import clockImage from 'assets/images/icons/clock.svg';
import { Tooltip } from 'components/core/Tooltip';
import QuestionMark from 'components/svg/QuestionMark';
import { Button } from 'components/core/Button';

import './bookableUnitDetailsV2.scss';

export function BookableUnitDetailsV2({
	unit,
	reservationFormShowing = false,
	onCreateBooking,
	handleToggleUnitReservationDrawer: handleToggleDrawerProp,
	facility,
	storageType,
	reservationFormRef,
	savedFormValues,
	onFormValuesChange,
}) {
	const handleToggleUnitReservationDrawer = useCallback(() => {
		handleToggleDrawerProp(unit, storageType);
	}, [handleToggleDrawerProp, unit, storageType]);
	const isScarce = getScarcityFlag([unit]);
	const { regular, meta } = unit.priceDetails;
	const isOnlineMoveIn = facility.hasOnlineMoveins;

	const [isAmenitiesExpanded, setIsAmenitiesExpanded] = useState(false);

	const handleToggleAmenities = () => {
		setIsAmenitiesExpanded((prev) => !prev);
	};

	return (
		<Card
			className={classnames('bookable-unit-details-content-v2', {
				'amenities-expanded': isAmenitiesExpanded,
			})}
		>
			{!!unit.tierName && (
				<div className="tier-name">
					<Paragraph>{unit.tierName}</Paragraph>
					{unit.tierDescription && (
						<Tooltip
							content={
								<span style={{ color: '#000000' }}>
									{unit.tierDescription}
								</span>
							}
							size="medium"
							backgroundColor="white"
							className="tier-tooltip-no-border"
						>
							<span className="question-mark">
								<QuestionMark showTitle={false} />
							</span>
						</Tooltip>
					)}
				</div>
			)}
			<UnitListItem>
				<div className="unit-details-content">
					<div className="left-side-content">
						<div className="unit-img-container-content">
							<UnitIcon
								unitSize={`${unit.sizeBucket}-${unit.unitType}`}
								alt="Unit Size"
								lazyLoad={false}
							/>
						</div>
						<UnitDescriptionV2
							unitSize={unit.sizeText}
							unitType={unit.unitTypeText}
							amenitiesText={unit.amenitiesText}
							promotion={trim(unit.promotion)}
							tierDescription={unit.tierDescription}
							tierName={unit.tierName}
							giftCardOffer={facility?.giftCardOffer}
							pricePerSqft={
								unit.pricePerSqFt?.sparefoot ||
								unit.pricePerSqFt?.regular
							}
							onToggleAmenities={handleToggleAmenities}
							showAllAmenities={isAmenitiesExpanded}
						/>
					</div>
					<div className="right-side-content">
						<div className="right-side-new">
							<div className="price-column">
								<UnitPriceV2
									price={meta.text}
									regularPrice={
										regular && regular.value !== meta.value
											? regular.text
											: null
									}
									linkedPrice={false}
									showPeriod
									unitQuantity={unit.quantity}
									isScarce={isScarce}
									showFees={facility.showFeeWarning}
									fees={facility.fees}
								/>
							</div>

							<div className="unit-card-reservation-button">
								<Button
									className={classnames(
										'ph-button button-primary',
										{
											'button-gray':
												reservationFormShowing,
											'button-pink':
												!reservationFormShowing,
										},
									)}
									id="render-reservation-form-button"
									onClick={handleToggleUnitReservationDrawer}
									aria-expanded={reservationFormShowing}
									aria-controls="reservation-form-container"
									aria-label="Open reservation form to reserve unit"
								>
									{reservationFormShowing
										? 'Close'
										: 'Reserve Now, Pay Later'}
								</Button>
								<span className="note">
									You will not be charged until move-in
								</span>
							</div>
						</div>
					</div>
				</div>
			</UnitListItem>
			<Expand showing={reservationFormShowing}>
				<div className="reservation-form-container">
					<IconText
						size="small"
						className="lock-in-rate"
						src={clockImage}
					>
						Lock in this rate now. No credit card. No obligation.
					</IconText>
					<UnitReservationForm
						ref={reservationFormRef}
						onSubmit={onCreateBooking}
						unit={unit}
						facility={facility}
						isOMI={isOnlineMoveIn}
						onCreateBooking={onCreateBooking}
						isScarce={isScarce}
						savedFormValues={savedFormValues}
						onFormValuesChange={onFormValuesChange}
					/>
				</div>
			</Expand>
		</Card>
	);
}

BookableUnitDetailsV2.propTypes = {
	unit: PropTypes.object.isRequired,
	facility: PropTypes.object.isRequired,
	handleToggleUnitReservationDrawer: PropTypes.func.isRequired,
	reservationFormShowing: PropTypes.bool,
	onCreateBooking: PropTypes.func,
	storageType: PropTypes.string,
	reservationFormRef: PropTypes.object,
};

export default React.memo(BookableUnitDetailsV2);
